interface ParameterExclusionConditions {
  [key: string]: string | number
}

export type Parameter = string | string[] | Record<string, string | string[]>
export type Parameters = Record<string, Parameter>

interface Hash {
  anchor: string | null
  parameters: Parameters
  parameterExclusionConditions: ParameterExclusionConditions
}

interface HashManagerConfig {
  updateHashOnParametersChange: boolean
}

const savedHash: Hash = {
  anchor: null,
  parameters: {},
  parameterExclusionConditions: {},
}

const defaultConfig: HashManagerConfig = {
  updateHashOnParametersChange: true,
}

export default {
  config: defaultConfig,

  init({ parameterExclusionConditions = {} } = {}, config?: HashManagerConfig) {
    if (!config) {
      config = defaultConfig
    }

    this.setConfig(config)
    savedHash.parameterExclusionConditions = parameterExclusionConditions
    this.parseHash()
  },

  parseNestedQueryString(queryString: string): Parameters {
    const params = new URLSearchParams(queryString)
    const result: Parameters = {}

    for (const [key, value] of params.entries()) {
      const keys = key.replace(/\]/g, '').split('[')

      keys.reduce((acc, currentKey, index) => {
        if (index === keys.length - 1) {
          acc[currentKey] = value
          return acc
        }

        if (!acc[currentKey]) {
          const nextKey = keys[index + 1]
          if (nextKey) {
            acc[currentKey] = /^\d+$/.test(nextKey) ? [] : {}
          }
        }

        return acc[currentKey] as Parameters
      }, result)
    }
    return result
  },

  parseHash() {
    const hash = window.location.hash.match(/#([a-zA-Z0-9-]*)(\?.*)?/)
    savedHash.anchor = null
    savedHash.parameters = {}

    if (hash) {
      if (hash[1]) {
        savedHash.anchor = hash[1]

        requestAnimationFrame(() => {
          this.goToAnchor(savedHash.anchor as string)
        })
      }

      if (hash[2]) {
        savedHash.parameters = this.parseNestedQueryString(decodeURIComponent(hash[2]))
      }
    }

    return this
  },

  setAnchor(anchor: string) {
    savedHash.anchor = anchor

    this.updateHash()

    return this
  },

  getAnchor() {
    return savedHash.anchor
  },

  goToAnchor(anchor: string): void {
    const anchorEl = document.getElementById(anchor)

    if (anchorEl) {
      anchorEl.scrollIntoView({
        block: 'start',
        inline: 'nearest',
      })
    }
  },

  removeAnchor() {
    savedHash.anchor = null

    this.updateHash()

    return this
  },

  flattenParametersToStringEntries(obj: Record<string, unknown>, prefix = ''): [string, string][] {
    let entries: [string, string][] = []

    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const value = obj[key] as Record<string, unknown>
        // Create the key path, e.g., "filters" or "filters[types]"
        const newPrefix = prefix ? `${prefix}[${key}]` : key

        if (value !== null && typeof value === 'object') {
          // If the value is an object or array, recurse
          entries = entries.concat(this.flattenParametersToStringEntries(value, newPrefix))
        } else if (value !== '' && value != null) {
          // This is a primitive value, add it to the list
          entries.push([newPrefix, String(value)])
        }
      }
    }
    return entries
  },

  updateHash() {
    let hash = ''

    if (savedHash.anchor) {
      hash = savedHash.anchor + hash
    }

    const parameters = this.getParametersWithExclusion()

    if (Object.keys(parameters).length > 0) {
      const entries = this.flattenParametersToStringEntries(parameters).sort(([k1], [k2]) => k1.localeCompare(k2))

      hash = `${hash}?${new URLSearchParams(entries).toString()}`
    }

    history.pushState({}, '', `#${hash}`)

    window.dispatchEvent(new HashChangeEvent('hashchange'))

    return this
  },

  hasParameters() {
    return Object.keys(savedHash.parameters).length > 0
  },

  setParameters(parameters = {}, replaceAll = false) {
    if (replaceAll) {
      savedHash.parameters = parameters
    } else {
      savedHash.parameters = { ...savedHash.parameters, ...parameters }
    }

    if (!this.config.updateHashOnParametersChange) {
      return this
    }

    this.updateHash()

    return this
  },

  getParametersWithExclusion() {
    const parameters = this.getParameters()

    Object.keys(savedHash.parameterExclusionConditions).forEach((key) => {
      if (Object.hasOwn(parameters, key) && parameters[key] === savedHash.parameterExclusionConditions[key]) {
        delete parameters[key]
      }
    })

    return parameters
  },

  getParameters() {
    return Object.assign({}, savedHash.parameters)
  },

  removeParameters() {
    savedHash.parameters = {}

    this.updateHash()

    return this
  },

  getParameter<T>(key: string) {
    if (!savedHash.parameters[key]) {
      return null
    }

    return savedHash.parameters[key] as T
  },

  setConfig(config: HashManagerConfig) {
    this.config = config
  },

  startUpdatingHash() {
    this.config.updateHashOnParametersChange = true
  },

  stopUpdatingHash() {
    this.config.updateHashOnParametersChange = false
  },

  destroy() {
    savedHash.anchor = null
    savedHash.parameters = {}

    return this
  },
}
